import { Dispatch, SetStateAction } from "react";
import { Box, useTheme } from "@mui/material";
import { JsonAttributeField, type JsonAttributeFormValue } from "src/components/JsonAttributes";
import { useFoodpilotContext } from "src/context/FoodpilotContext";
import { ProductFormType } from "src/pages/Product/ProductForm/ProductForm.types";
import { JSON_ATTRIBUTE_ENTITY_CLASS } from "src/constants";

type Step2AdditionalInformationsProps = {
    product: ProductFormType;
    setProduct: Dispatch<SetStateAction<ProductFormType>>;
};

/**
 * Step 2 - Additional informations (Figma empty `12819:67434`, filled
 * `13154:221645`).
 *
 * Mounted conditionally by `useWizardStepsV2` only when admin-defined JSON attributes
 * exist for the Product entity. No Zod validation lives here; the global Save gate
 * (`validateProductForm`) does not enforce custom-attribute constraints.
 *
 * Returns bare section contents — `SectionCard` + `SectionHeader` are provided by
 * `ProductFormV2`.
 */
export const Step2AdditionalInformations = (props: Step2AdditionalInformationsProps) => {
    const { product, setProduct } = props;
    const theme = useTheme();
    const { entitiesJsonAttributes } = useFoodpilotContext();
    const productAttributes = entitiesJsonAttributes?.[JSON_ATTRIBUTE_ENTITY_CLASS.product] ?? [];

    if (productAttributes.length === 0) {
        return null;
    }

    const updateAttribute = (identifier: string, value: JsonAttributeFormValue) => {
        setProduct((previousProduct) => ({
            ...previousProduct,
            jsonAttributes: {
                ...previousProduct.jsonAttributes,
                [identifier]: value,
            },
        }));
    };

    return (
        <Box
            data-testid="additional-information-fields"
            sx={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                columnGap: theme.spacing(2),
                rowGap: theme.spacing(3),
                padding: theme.spacing(3),
            }}
        >
            {productAttributes.map((attribute) => (
                <JsonAttributeField
                    appearance="product-v2"
                    key={attribute.identifier}
                    attribute={attribute}
                    value={product.jsonAttributes?.[attribute.identifier]}
                    onChange={(value) => updateAttribute(attribute.identifier, value)}
                />
            ))}
        </Box>
    );
};
